// eslint-disable-next-line no-restricted-imports -- RVSelectCreate is the wrapper component that needs to import Select from antd
import { useState, useCallback } from "react";

import { Select, Typography } from "antd";
import { clsx } from "clsx";

import { RVColumn } from "../../layouts/RVFlexbox/RVFlexbox";
import { RVButton } from "../../molecules/RVButton/RVButton";

import type { SelectProps } from "antd/lib/select";

import styles from "./RVSelectCreate.module.scss";

export type SelectValueType = {
    label: string;
    value: string;
};

type SelectCreateProps = SelectProps<string> & {
    /** Array of options to be displayed in the select dropdown */
    options: Array<any>;
    /** Text displayed on the creation button */
    creationText?: string;
    /** Flag to hide the empty data message */
    hideEmptyDataMessage?: boolean;
    /** Called when a new option is created */
    onCreate: (attr: string) => void;
    /** Called when an option is selected */
    onSelect: (attr: string) => void;
};

const DEFAULT_CREATION_TEXT = "Add Option";

/**
 * RVSelectCreate is a custom dropdown selector component that allows users to search,
 * select, and create new options if they don't find what they're looking for.
 * If you don't need the creation functionality, use the TypeaheadSelect component.
 */
export function RVSelectCreate({
    options,
    creationText = DEFAULT_CREATION_TEXT,
    hideEmptyDataMessage,
    onCreate,
    onSelect,
    ...props
}: SelectCreateProps) {
    const [search, setSearch] = useState<string | undefined>();

    const [isOpen, setIsOpen] = useState(false);

    const handleSearch = useCallback((value: string) => {
        if (value !== "") {
            setIsOpen(true);
        }
        setSearch(value);
    }, []);

    const handleAddTag = useCallback(async () => {
        onCreate(search || "");
        setSearch("");
        setIsOpen(false);
    }, [onCreate, search]);

    const handleSelect = useCallback(
        (value: string) => {
            setIsOpen(false);
            onSelect(value);
        },
        [onSelect]
    );

    const handleFocus = useCallback(() => {
        setIsOpen(true);
    }, []);

    const handleBlur = useCallback(() => {
        // we need to delay this or the created propery will not get selected
        setTimeout(() => setIsOpen(false), 100);
    }, []);

    return (
        <Select
            open={isOpen}
            allowClear
            onFocus={handleFocus}
            onBlur={handleBlur}
            onSelect={handleSelect}
            showSearch
            onSearch={handleSearch}
            searchValue={search}
            options={options}
            className={clsx(styles.tagSelector, {
                [styles.hideEmptyDataMessage]: hideEmptyDataMessage,
            })}
            // fixes the dropdown position and resolves weird repositioning issues
            getPopupContainer={(trigger: any) => trigger.parentNode}
            virtual={false} // Disable virtual scroll because it causes rendering issues after filtering
            {...props}
            dropdownRender={(menu: JSX.Element) => {
                return (
                    <RVColumn gap={1}>
                        {menu}
                        {search && (
                            <div className={styles.addTagRow}>
                                <div className={styles.newTagRow}>
                                    <Typography.Text className={styles.searchText}>{search}</Typography.Text>
                                    {/* remove the right side padding so that the rows padding doesnt double pad the button */}
                                    <RVButton className={styles.addTagButton} type={"link"} onClick={handleAddTag}>
                                        {creationText}
                                    </RVButton>
                                </div>
                            </div>
                        )}
                    </RVColumn>
                );
            }}
        ></Select>
    );
}
